import type { Route } from "../../../../../utils/models/Route";
import type { LockedBoundary } from "../../../utils/masterTrail";
import type { UndoCommand, CommandContext } from "../types";

export interface LockedRouteChange {
  routeKey: string;
  before: Route;
  after: Route;
}

/**
 * Command for a locked-mode boundary drag (see
 * store/modules/actions/locked-boundary-drag.ts): repositions one entry in
 * state.lockedBoundaries and fully replaces the markers/lineStrings of the
 * one or two day routes rebuilt from it, plus the `day` of any alt/spur legs
 * regrouped onto a different day as a result. Undo/redo restore full route
 * snapshots rather than fixed-index endpoint changes, since a boundary drag
 * can add/remove a detour leg and so change how many markers/lineStrings a
 * route has.
 */
export class MoveLockedBoundaryCommand implements UndoCommand {
  readonly type: string = 'MOVE_LOCKED_BOUNDARY';
  readonly routeKey: string | null = null;

  private dayNumber: number;
  private boundaryBefore: LockedBoundary;
  private boundaryAfter: LockedBoundary;
  private routeChanges: LockedRouteChange[];

  constructor(payload: {
    dayNumber: number;
    boundaryBefore: LockedBoundary;
    boundaryAfter: LockedBoundary;
    routeChanges: LockedRouteChange[];
  }) {
    this.dayNumber = payload.dayNumber;
    this.boundaryBefore = JSON.parse(JSON.stringify(payload.boundaryBefore));
    this.boundaryAfter = JSON.parse(JSON.stringify(payload.boundaryAfter));
    this.routeChanges = payload.routeChanges.map(change => ({
      routeKey: change.routeKey,
      before: JSON.parse(JSON.stringify(change.before)),
      after: JSON.parse(JSON.stringify(change.after)),
    }));
  }

  undo(ctx: CommandContext): void {
    this.apply(ctx, 'before');
  }

  redo(ctx: CommandContext): void {
    this.apply(ctx, 'after');
  }

  private apply(ctx: CommandContext, direction: 'before' | 'after'): void {
    const boundary = direction === 'before' ? this.boundaryBefore : this.boundaryAfter;
    ctx.state.lockedBoundaries = {
      ...ctx.state.lockedBoundaries,
      [this.dayNumber]: JSON.parse(JSON.stringify(boundary)),
    };

    for (const change of this.routeChanges) {
      const route = ctx.state.routes[change.routeKey];
      if (!route) continue;
      const snapshot = direction === 'before' ? change.before : change.after;
      route.markers = JSON.parse(JSON.stringify(snapshot.markers));
      route.lineStrings = JSON.parse(JSON.stringify(snapshot.lineStrings));
      route.day = snapshot.day;
      route.updated_at = new Date();
    }
    ctx.state._statsVersion++;
  }
}
